import { useCheckoutContext } from '../index'
import { useAccessToken, useError } from '@ztechapps/common.hook'
import compact from 'lodash/compact'
import { fetch } from '@ztechapps/common.utils'
import isEmpty from 'lodash/isEmpty'
import trim from 'lodash/trim'

type StripeLineItem = { price: number; name: string; description?: string; quantity?: number }

export type UseCreatePaymentLinkResult = {
  createPaymentLink: (partialPaymentLineItem?: StripeLineItem) => Promise<string>
  hasError: boolean
  error: any
}

const useCreatePaymentLinkBase = (): UseCreatePaymentLinkResult => {
  const checkoutContext = useCheckoutContext()
  const { document } = checkoutContext.orderDocumentResult
  const { error, onError } = useError()
  const fromCheckout = checkoutContext.fromCheckout
  const orderNetAmount = checkoutContext.orderNetAmount

  const [, setShowLoadingOverlay] = checkoutContext.showLoadingOverlay
  const accessToken = useAccessToken()
  const orderId = document?._id

  const createPaymentLink = async (partialPaymentLineItem?: StripeLineItem): Promise<string> => {
    try {
      setShowLoadingOverlay(true)

      const newLineItems = compact(isEmpty(partialPaymentLineItem) ? [] : [partialPaymentLineItem])

      const url = `/api/stripe/create-payment-link`

      const { url: paymentLinkUrl }: any = await fetch(url, {
        headers: {
          'login-token': accessToken,
        },
        method: 'POST',
        body: JSON.stringify({
          amount: orderNetAmount,
          orderId,
          userId: document?.by?._id,
          line_items: newLineItems,
          fromCheckout,
        }),
      })

      return paymentLinkUrl
    } catch (e) {
      onError(e)
      throw e
    } finally {
      setShowLoadingOverlay(false)
    }
  }

  return { createPaymentLink, hasError: !isEmpty(error), error }
}

export const useCreatePaymentLink = (): UseCreatePaymentLinkResult => {
  const { createPaymentLink, ...rest } = useCreatePaymentLinkBase()
  const checkoutContext = useCheckoutContext()
  const [paymentOptions] = checkoutContext.paymentOptions
  const hasPartialPayments = checkoutContext.hasPartialPayments

  const remarks = paymentOptions.remarks
  const amount = paymentOptions.amount
  const partialPaymentLineItem: StripeLineItem | undefined = hasPartialPayments
    ? {
        name: isEmpty(trim(remarks)) ? 'Additional payment.' : remarks,
        price: amount,
      }
    : undefined

  const _createPaymentLink = () => createPaymentLink(partialPaymentLineItem)

  return { ...rest, createPaymentLink: _createPaymentLink }
}

export const useCreatePartialPaymentLink = (): UseCreatePaymentLinkResult => {
  const checkoutContext = useCheckoutContext()
  const [paymentOptions] = checkoutContext.paymentOptions

  const { createPaymentLink, ...rest } = useCreatePaymentLinkBase()

  const _createPaymentLink = (partialPaymentLineItem?: StripeLineItem) => {
    const remarks = partialPaymentLineItem?.name ?? paymentOptions.remarks
    const amount = partialPaymentLineItem?.price ?? paymentOptions.amount
    const lineItem: StripeLineItem = {
      name: isEmpty(trim(remarks)) ? 'Additional payment.' : remarks,
      price: amount,
      ...(partialPaymentLineItem?.description && { description: partialPaymentLineItem.description }),
      ...(partialPaymentLineItem?.quantity && { quantity: partialPaymentLineItem.quantity }),
    }

    return createPaymentLink(lineItem)
  }

  return { ...rest, createPaymentLink: _createPaymentLink }
}
